/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AccordionItem {
  id: string;
  isOpen: boolean;
  groupId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccordionService {
  private accordionItems = new Map<string, BehaviorSubject<AccordionItem>>();
  private accordionGroups = new Map<string, Set<string>>();
  private currentOpenComponent = new BehaviorSubject<string | null>(null);

  constructor() {}

  /**
   * Register a new accordion item
   *
   * @param id Unique identifier for the accordion item
   * @param groupId Optional group identifier - items in the same group will follow accordion behavior
   * @param initialState Initial open/closed state
   */
  registerItem(
    id: string,
    groupId?: string,
    initialState: boolean = false
  ): Observable<AccordionItem> {
    if (!this.accordionItems.has(id)) {
      const item: AccordionItem = { id, isOpen: initialState, groupId };
      this.accordionItems.set(id, new BehaviorSubject<AccordionItem>(item));

      // Register item to group if groupId is provided
      if (groupId) {
        if (!this.accordionGroups.has(groupId)) {
          this.accordionGroups.set(groupId, new Set<string>());
        }
        this.accordionGroups.get(groupId)?.add(id);
      }
    }

    const itemSubject = this.accordionItems.get(id);
    return itemSubject
      ? itemSubject.asObservable()
      : new BehaviorSubject<AccordionItem>({
          id,
          isOpen: false,
          groupId,
        }).asObservable();
  }

  /**
   * Toggle an accordion item's state
   *
   * @param id Accordion item identifier
   */
  toggleItem(id: string): void {
    const itemSubject = this.accordionItems.get(id);
    if (!itemSubject) {
      return;
    }

    const currentItem = itemSubject.value;
    const newState = !currentItem.isOpen;

    // If opening an item and it belongs to a group, close all other items in the group
    if (newState && currentItem.groupId) {
      this.closeAllInGroup(currentItem.groupId, id);
    }

    // Update the current item
    itemSubject.next({ ...currentItem, isOpen: newState });
  }

  /**
   * Open a specific accordion item
   *
   * @param id Accordion item identifier
   */
  openItem(id: string): void {
    const itemSubject = this.accordionItems.get(id);
    if (!itemSubject) {
      return;
    }

    const currentItem = itemSubject.value;

    // If item belongs to a group, close all other items in the group
    if (currentItem.groupId) {
      this.closeAllInGroup(currentItem.groupId, id);
    }

    // Open the current item
    itemSubject.next({ ...currentItem, isOpen: true });
  }

  /**
   * Close a specific accordion item
   *
   * @param id Accordion item identifier
   */
  closeItem(id: string): void {
    const itemSubject = this.accordionItems.get(id);
    if (!itemSubject) {
      return;
    }

    const currentItem = itemSubject.value;
    itemSubject.next({ ...currentItem, isOpen: false });
  }

  /**
   * Close all items in a group
   *
   * @param groupId Group identifier
   */
  closeGroup(groupId: string): void {
    this.closeAllInGroup(groupId);
  }

  /**
   * Get the current state of an accordion item
   *
   * @param id Accordion item identifier
   */
  getItemState(id: string): boolean {
    const itemSubject = this.accordionItems.get(id);
    return itemSubject ? itemSubject.value.isOpen : false;
  }

  /**
   * Unregister an accordion item (useful for cleanup)
   *
   * @param id Accordion item identifier
   */
  unregisterItem(id: string): void {
    const itemSubject = this.accordionItems.get(id);
    if (itemSubject) {
      const item = itemSubject.value;

      // Remove from group if it belongs to one
      if (item.groupId) {
        const groupItems = this.accordionGroups.get(item.groupId);
        if (groupItems) {
          groupItems.delete(id);
          if (groupItems.size === 0) {
            this.accordionGroups.delete(item.groupId);
          }
        }
      }

      // Complete the subject and remove from map
      itemSubject.complete();
      this.accordionItems.delete(id);
    }
  }

  /**
   * Clear all accordion items for a specific group
   *
   * @param groupId Group identifier
   */
  clearGroup(groupId: string): void {
    const groupItems = this.accordionGroups.get(groupId);
    if (groupItems) {
      groupItems.forEach((itemId) => {
        this.unregisterItem(itemId);
      });
    }
  }

  // New method to manage accordion behavior
  public openComponent(componentId: string): void {
    this.currentOpenComponent.next(componentId);
  }

  public closeComponent(componentId: string): void {
    if (this.currentOpenComponent.value === componentId) {
      this.currentOpenComponent.next(null);
    }
  }

  public isComponentOpen(componentId: string): boolean {
    return this.currentOpenComponent.value === componentId;
  }

  public getCurrentOpenComponent(): Observable<string | null> {
    return this.currentOpenComponent.asObservable();
  }

  /**
   * Close all items in a group except the specified one
   *
   * @param groupId Group identifier
   * @param exceptId Item to exclude from closing
   */
  private closeAllInGroup(groupId: string, exceptId?: string): void {
    const groupItems = this.accordionGroups.get(groupId);
    if (!groupItems) {
      return;
    }

    groupItems.forEach((itemId) => {
      if (itemId !== exceptId) {
        const itemSubject = this.accordionItems.get(itemId);
        if (itemSubject) {
          const currentItem = itemSubject.value;
          itemSubject.next({ ...currentItem, isOpen: false });
        }
      }
    });
  }
}
